import React, {useState, useEffect, useRef, useCallback} from 'react';
import {TypeImplementation, Expressions} from '@packages/systems/wfdl';
import {EText, TText} from '@packages/systems/wfdl-ast';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import UnitInput from '@packages/systems/design-system/WebflowUnitInput';
import {Err, Ok} from '@packages/utilities/fp/result';
import {isValidNumber} from '@packages/systems/core/utils/NumberUtils';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';

const AUTO_VALUE = {unit: 'auto', value: 'Auto'} as const;
const DEFAULT_PX = 100; //if there is NOT a current or pre-existing numeric value, use 100 (could be any number)

const unitData = [
  {
    menuName: 'PX',
    unit: 'px',
    range: {min: 0, max: Number.MAX_SAFE_INTEGER, step: 1},
  },
  {menuName: 'AUTO', buttonName: '-', unit: 'auto'},
];

const getUnit = ({value, unit}: {value: unknown; unit: string}) =>
  value === 'auto' ? value : unit;

// @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
const getNumberFromValue = ({value}) => (value === 'auto' ? undefined : value);

const getValue = (value: undefined | number | string, unit: string) => {
  if (unit === 'auto') {
    return AUTO_VALUE;
  }

  return {value, unit};
};

const parseValue = (unit: string, value: string) => {
  const normalizedInput = value.trim().toLowerCase();

  if (normalizedInput === 'auto') {
    return Ok(AUTO_VALUE);
  }

  if (normalizedInput.endsWith('px')) {
    const inputAsNumber = parseFloat(normalizedInput);

    return inputAsNumber
      ? Ok({
          unit: 'px',
          value: inputAsNumber,
        })
      : Err(`Could not parse number from "${normalizedInput}"`);
  }

  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
  return isNaN(value)
    ? Err(`Could not parse number from "${normalizedInput}"`)
    : Ok({
        unit: 'px',
        value,
      });
};

const isValidInputValue = (unit: string, value: string) => {
  return value === 'auto' || isValidNumber(value);
};

const RenderAutoOrNumber = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const initialValue = Expressions.getText(stripAll(expr));
  const prevNumRef = useRef(undefined);
  const [inputValue, setInputValue] = useState(initialValue);
  const [inputUnit, setInputUnit] = useState(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    isValidNumber(initialValue) ? 'px' : 'auto'
  );

  /**
   * If the value from the expression changes, re-sync it to state so the input gets updated
   */
  React.useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const onChange = useCallback(
    ({unit, value}: {unit: string; value: string | number}) => {
      let newValue = value;

      if (unit === 'px') {
        newValue = value || prevNumRef.current || DEFAULT_PX;
      }

      const stringValue = String(newValue);

      setInputValue(stringValue);
      setInputUnit(unit);
      dispatch(
        updateExpressionAction(EText(stringValue), {
          analytics: {
            type: 'element-settings-updated',
            value: 'image dimensions',
          },
        })
      );
    },
    [dispatch, inputUnit, inputValue]
  );

  useEffect(() => {
    // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string | number'. | TS2322 - Type 'string | null' is not assignable to type 'undefined'.
    if (isValidNumber(inputValue)) prevNumRef.current = inputValue;
  }, [inputUnit, inputValue]);

  return (
    <UnitInput
      name="Auto-Or-Number"
      data-automation-id="image-unit-input"
      onChange={onChange}
      isValidInputValue={isValidInputValue}
      getValue={getValue}
      getUnit={getUnit}
      units={unitData}
      value={{unit: inputUnit, value: inputValue || ''}}
      parseValue={parseValue}
      getNumberFromValue={getNumberFromValue}
      placeholder={AUTO_VALUE}
    />
  );
};

export const AutoOrNumber = TypeImplementation.Alias({
  type: TText,
  render: RenderAutoOrNumber,
});
